<template>
  <div>
    <b-container>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Relación de personas a asegurar</h6>
            </template>
            <p class="text-muted small mb-3">
              Agregue cada persona con el botón inferior. Cada persona en la lista debe estar completa. Máximo
              {{ maxDependienes }} personas.
            </p>

            <b-row class="mb-3">
              <b-col cols="12">
                <RadioComponent
                  :form="form"
                  name="titularEsAsegurado"
                  label="¿El titular de la póliza será uno de los asegurados?"
                  rules="required"
                  :options="control.TIPOS_SINO"
                />
              </b-col>
            </b-row>

            <div
              v-if="!form.dependientes || form.dependientes.length === 0"
              class="text-center py-4 border rounded mb-3"
            >
              <p class="text-muted mb-3 mb-md-0">Aún no hay personas cargadas.</p>
              <b-button type="button" variant="primary" @click="agregarPersona">Agregar primera persona</b-button>
            </div>

            <div
              v-for="(item, index) in form.dependientes"
              :key="item._uid || 'persona-' + index"
              class="persona-asegurar-card mb-3"
            >
              <b-card no-body class="border persona-asegurar-inner">
                <b-card-header
                  class="d-flex align-items-center justify-content-between py-2 persona-asegurar-header bg-light"
                >
                  <span class="font-weight-medium mb-0">Persona {{ index + 1 }}</span>
                  <b-button
                    type="button"
                    pill
                    variant="outline-danger"
                    size="sm"
                    :disabled="esFilaTitularBloqueada(index)"
                    @click.stop="quitarPersona(index)"
                  >
                    Quitar persona
                  </b-button>
                </b-card-header>
                <b-card-body class="pt-3 pb-3">
                  <b-row>
                    <b-col cols="12" md="6" lg="4">
                      <TextComponent
                        :form="item"
                        name="name"
                        label="Nombre(s)"
                        rules="alpha_spaces|max:50|required"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="4">
                      <TextComponent
                        :form="item"
                        name="lastName"
                        label="Primer apellido"
                        rules="alpha_spaces|max:25|required"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="4">
                      <TextComponent
                        :form="item"
                        name="secondLastName"
                        label="Segundo apellido"
                        rules="alpha_spaces|max:25"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="12" md="6" lg="3">
                      <SelectComponent
                        :form="item"
                        name="documentType"
                        label="Tipo documento"
                        rules="required"
                        :options="control.docTypeOptions"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="3">
                      <TextComponent
                        :form="item"
                        name="document"
                        label="Nº identificación"
                        rules="required|max:15"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="3">
                      <TextComponent
                        type="date"
                        :form="item"
                        name="birthDay"
                        label="Fecha de nacimiento"
                        rules="required|date_min:01/01/1900,DD/MM/YYYY|date_max:today"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="3">
                      <SelectComponent
                        :form="item"
                        name="relation"
                        label="Parentesco"
                        rules="required"
                        :options="parentescoOptionsParaFila(index)"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="2">
                      <TextComponent
                        :form="item"
                        name="weight"
                        type="number"
                        step="0.1"
                        :min="35"
                        :max="300"
                        label="Peso (kg)"
                        rules="required|min_value:35|max_value:300"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="2">
                      <TextComponent
                        :form="item"
                        name="stature"
                        type="number"
                        step="0.01"
                        :min="1"
                        :max="2.5"
                        label="Estatura (m)"
                        rules="required|min_value:1|max_value:2.5"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="2">
                      <SelectComponent
                        :form="item"
                        name="sex"
                        label="Sexo"
                        rules="required"
                        :options="control.sexoOptions"
                        :disabled="esFilaTitularBloqueada(index)"
                      />
                    </b-col>
                    <b-col cols="12" md="6" lg="6">
                      <TextComponent :form="item" name="eps" label="EPS" rules="required|max:50" />
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </div>

            <div v-if="form.dependientes && form.dependientes.length > 0" class="mb-3">
              <b-button
                type="button"
                variant="secondary"
                :disabled="form.dependientes.length >= maxDependienes"
                @click="agregarPersona"
              >
                Agregar persona
              </b-button>
              <span v-if="form.dependientes.length >= maxDependienes" class="text-muted small ml-2">
                Límite de {{ maxDependienes }} personas alcanzado.
              </span>
            </div>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button type="button" @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import {
  TIPOS_DEPENDIENTES,
  CANTIDAD_FILAS_CUESTIONARIO_SINO,
  TIPOS_IDENTIFICACIONES,
  TIPOS_SEXO,
  TIPOS_SINO,
} from "@/data/colombia/salud/Selects";

import RadioComponent from "@/components/controls/RadioComponent.vue";
import SelectComponent from "@/components/controls/SelectComponent.vue";
import TextComponent from "@/components/controls/TextComponent.vue";


const MAX_DEPENDIENTES = 6;
/** Parentesco "Asegurado Principal" (titular en la lista). */
const RELACION_ASEGURADO_PRINCIPAL = "A1";
/** Fila Sí/No del PDF para pregunta 23 (trastornos femeninos; solo mujeres). */
const PDF_ROW_CUEST_MUJERES = 22;

function splitApellidosTitular(apellidos) {
  const t = (apellidos || "").trim();
  if (!t) return { lastName: "", secondLastName: "" };
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { lastName: parts[0], secondLastName: "" };
  return { lastName: parts[0], secondLastName: parts.slice(1).join(" ") };
}

function nombresTitularParaDependiente(form) {
  const a = (form.firstName || "").trim();
  const b = (form.secondFirstName || "").trim();
  return [a, b].filter(Boolean).join(" ").trim();
}

function respuestasCuestionarioVacias() {
  return Array(CANTIDAD_FILAS_CUESTIONARIO_SINO).fill("");
}

function personaVacia() {
  return {
    _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: "",
    lastName: "",
    secondLastName: "",
    documentType: "",
    document: "",
    birthDay: "",
    relation: "",
    weight: "",
    stature: "",
    sex: "",
    eps: "",
    datosDesdeTitular: false,
    respuestas: respuestasCuestionarioVacias(),
  };
}

/** Fila sin ningún dato (legacy: plantillas vacías rellenadas a 6). */
function filaDependienteVacia(p) {
  if (!p) return true;
  return !(
    (p.name && String(p.name).trim()) ||
    (p.lastName && String(p.lastName).trim()) ||
    (p.documentType && String(p.documentType).trim()) ||
    (p.document && String(p.document).trim()) ||
    p.birthDay ||
    p.relation ||
    (p.weight !== undefined && p.weight !== null && p.weight !== "") ||
    (p.stature !== undefined && p.stature !== null && p.stature !== "") ||
    p.sex ||
    (p.eps && String(p.eps).trim())
  );
}

export default {
  name: "FormColombiaSalud02",
  components: { RadioComponent, SelectComponent, TextComponent },
  data() {
    return {
      maxDependienes: MAX_DEPENDIENTES,
      control: {
        TIPOS_SINO,
        sexoOptions: [{ value: null, text: "Seleccione" }, ...TIPOS_SEXO],
        docTypeOptions: [{ value: null, text: "Seleccione" }, ...TIPOS_IDENTIFICACIONES],
      },
      form: {},
      sexosPrevDependientes: [],
    };
  },
  computed: {
    /** Solo los sexos: el watcher reacciona cuando cambia alguno sin recorrer todo el formulario en cada tecla. */
    listaSexosDependientes() {
      return (this.form.dependientes || []).map((p) => p.sex);
    },
  },
  watch: {
    "form.titularEsAsegurado"(val) {
      if (val === "S") {
        this.asegurarFilaTitularYSincronizar();
      } else if (val === "N") {
        const list = this.form.dependientes || [];
        const p = list[0];
        if (p && p.datosDesdeTitular) {
          this.form.dependientes = list.filter((_, i) => i !== 0);
          this.sexosPrevDependientes = (this.form.dependientes || []).map((x) => x.sex);
        } else if (p) {
          p.datosDesdeTitular = false;
        }
      }
      this.sanearRelacionAseguradoPrincipal();
    },
    listaSexosDependientes: {
      deep: true,
      handler(nuevos) {
        const list = this.form.dependientes || [];
        (nuevos || []).forEach((sex, index) => {
          const anterior = this.sexosPrevDependientes[index];
          if (anterior === "F" && sex === "M" && list[index]) {
            this.limpiarPreguntasCuestionarioMujer(list[index]);
          }
        });
        this.sexosPrevDependientes = (nuevos || []).slice();
      },
    },
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const { form } = this.$store.state.wizard;
    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/salud/");
      return;
    }
    this.form = form;
    if (
      (!this.form.dependientes || !Array.isArray(this.form.dependientes) || this.form.dependientes.length === 0) &&
      this.form.Dependientes &&
      Array.isArray(this.form.beneficiarios) &&
      this.form.beneficiarios.length > 0
    ) {
      this.form.dependientes = JSON.parse(JSON.stringify(this.form.beneficiarios));
    }
    this.form.beneficiarios = [];
    if (!this.form.dependientes || !Array.isArray(this.form.dependientes)) {
      this.form.dependientes = [];
    }
    this.form.dependientes = this.form.dependientes.filter((p) => !filaDependienteVacia(p));
    if (this.form.dependientes.length > MAX_DEPENDIENTES) {
      this.form.dependientes = this.form.dependientes.slice(0, MAX_DEPENDIENTES);
    }
    this.form.dependientes = this.form.dependientes.map((p) => {
      const _uid = p._uid || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const respuestas =
        !Array.isArray(p.respuestas) || p.respuestas.length !== CANTIDAD_FILAS_CUESTIONARIO_SINO
          ? Array(CANTIDAD_FILAS_CUESTIONARIO_SINO).fill("")
          : p.respuestas.slice();
      return { ...p, _uid, respuestas };
    });
    if (this.form.titularEsAsegurado !== "S" && this.form.titularEsAsegurado !== "N") {
      this.form.titularEsAsegurado = "";
    }
    if (this.form.titularEsAsegurado === "S") {
      this.asegurarFilaTitularYSincronizar();
    }
    this.sexosPrevDependientes = (this.form.dependientes || []).map((p) => p.sex);
    this.sanearRelacionAseguradoPrincipal();
    this.$store.dispatch("app/loading", false);
  },
  activated() {
    if (
      this.form &&
      this.form.titularEsAsegurado === "S" &&
      this.form.dependientes &&
      this.form.dependientes[0] &&
      this.form.dependientes[0].datosDesdeTitular
    ) {
      this.aplicarDatosTitularPrimeraFila();
    }
  },
  methods: {
    /**
     * "Asegurado Principal" (A1) solo aplica cuando el titular está en la lista (S) y en la primera fila.
     */
    parentescoOptionsParaFila(index) {
      const incluirA1 = this.form.titularEsAsegurado === "S" && index === 0;
      const lista = incluirA1
        ? TIPOS_DEPENDIENTES
        : TIPOS_DEPENDIENTES.filter((o) => o.value !== RELACION_ASEGURADO_PRINCIPAL);
      return [{ value: null, text: "Seleccione" }, ...lista];
    },
    /** Quita A1 en filas donde no corresponde o si el titular no figura como asegurado. */
    sanearRelacionAseguradoPrincipal() {
      const list = this.form.dependientes || [];
      const titularSi = this.form.titularEsAsegurado === "S";
      list.forEach((p, i) => {
        if (!p || p.relation !== RELACION_ASEGURADO_PRINCIPAL) return;
        if (!titularSi || i !== 0) {
          p.relation = "";
        }
      });
    },
    /**
     * Si ya existe la fila del titular (datosDesdeTitular en posición 0), solo sincroniza con Form01.
     * Si no, inserta una fila nueva al inicio (sin pisar dependientes ya cargados) y rellena hasta 6 máx.
     */
    asegurarFilaTitularYSincronizar() {
      if (!this.form.dependientes) this.form.dependientes = [];
      const list = this.form.dependientes;
      const p0 = list[0];
      if (p0 && p0.datosDesdeTitular) {
        this.aplicarDatosTitularPrimeraFila();
        return;
      }
      if (list.length >= MAX_DEPENDIENTES) {
        this.$bvToast.toast(
          "No se puede agregar al titular: el máximo es 6 personas. " +
            "Quite una fila o indique que el titular no figura aquí.",
          { title: "Límite alcanzado", variant: "warning", solid: true }
        );
        this.$nextTick(() => {
          this.$set(this.form, "titularEsAsegurado", "N");
        });
        return;
      }
      list.unshift(personaVacia());
      this.aplicarDatosTitularPrimeraFila();
      this.sexosPrevDependientes = list.map((p) => p.sex);
    },
    esFilaTitularBloqueada(index) {
      const p = this.form.dependientes && this.form.dependientes[index];
      return this.form.titularEsAsegurado === "S" && index === 0 && !!(p && p.datosDesdeTitular);
    },
    aplicarDatosTitularPrimeraFila() {
      if (!this.form.dependientes || !this.form.dependientes.length) return;
      const p = this.form.dependientes[0];
      const f = this.form;
      const { lastName, secondLastName } = splitApellidosTitular(f.lastName);
      p.name = nombresTitularParaDependiente(f);
      p.lastName = lastName;
      p.secondLastName = secondLastName;
      p.documentType = f.identificationType || "";
      p.document = f.identification != null ? String(f.identification).trim() : "";
      p.birthDay = f.birthDay || "";
      p.weight = f.weight !== undefined && f.weight !== null && f.weight !== "" ? String(f.weight) : "";
      p.stature = f.stature !== undefined && f.stature !== null && f.stature !== "" ? String(f.stature) : "";
      p.sex = f.sex || "";
      p.relation = RELACION_ASEGURADO_PRINCIPAL;
      p.datosDesdeTitular = true;
      if (!Array.isArray(p.respuestas) || p.respuestas.length !== CANTIDAD_FILAS_CUESTIONARIO_SINO) {
        p.respuestas = Array(CANTIDAD_FILAS_CUESTIONARIO_SINO).fill("");
      }
    },
    agregarPersona() {
      if (!this.form.dependientes) this.form.dependientes = [];
      if (this.form.dependientes.length >= MAX_DEPENDIENTES) return;
      this.form.dependientes.push(personaVacia());
    },
    quitarPersona(index) {
      if (this.esFilaTitularBloqueada(index)) return;
      const list = this.form.dependientes || [];
      const next = list.filter((_, i) => i !== index);
      this.form.dependientes = next;
      this.sexosPrevDependientes = next.map((p) => p.sex);
    },
    /**
     * Limpia preguntas 22–24 del cuestionario médico y campos asociados (mujeres).
     * Debe llamarse al pasar sexo de F a M.
     */
    limpiarPreguntasCuestionarioMujer(persona) {
      const base = Array.isArray(persona.respuestas) ? persona.respuestas.slice() : [];
      while (base.length < CANTIDAD_FILAS_CUESTIONARIO_SINO) base.push("");
      const r = base.slice(0, CANTIDAD_FILAS_CUESTIONARIO_SINO);
      r[PDF_ROW_CUEST_MUJERES] = "";
      persona.respuestas = r;
      persona.fechaPregunta22 = "";
      persona.detallePregunta24 = {
        nEmbarazos: "",
        nPartos: "",
        nAbortos: "",
        nCesareas: "",
      };
    },
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 1, form: this.form })
        .then(() => this.$router.push("/form/colombia-salud-1.html"));
    },
    getValidationName(index) {
      const p = this.form.dependientes[index];
      return p.name && String(p.name).trim() ? true : false;
    },
    getValidationDocumentType(index) {
      const p = this.form.dependientes[index];
      return p.documentType ? true : false;
    },
    getValidationDocument(index) {
      const p = this.form.dependientes[index];
      return p.document && String(p.document).trim() ? true : false;
    },
    getValidationBirthDay(index) {
      const p = this.form.dependientes[index];
      return p.birthDay ? true : false;
    },
    getValidationRelation(index) {
      const p = this.form.dependientes[index];
      return p.relation ? true : false;
    },
    getValidationWeight(index) {
      const p = this.form.dependientes[index];
      const w = p.weight;
      if (w === undefined || w === null || String(w).trim() === "") return false;
      const n = Number(w);
      return !Number.isNaN(n) && n >= 35 && n <= 300;
    },
    getValidationStature(index) {
      const p = this.form.dependientes[index];
      const s = p.stature;
      if (s === undefined || s === null || String(s).trim() === "") return false;
      const n = Number(s);
      return !Number.isNaN(n) && n >= 1 && n <= 2.5;
    },
    getValidationSex(index) {
      const p = this.form.dependientes[index];
      return p.sex ? true : false;
    },
    getValidationEps(index) {
      const p = this.form.dependientes[index];
      return !!(p.eps && String(p.eps).trim());
    },
    getValidationLastName(index) {
      const p = this.form.dependientes[index];
      return p.lastName && String(p.lastName).trim() ? true : false;
    },
    getValidationSecondLastName(index) {
      const p = this.form.dependientes[index];
      return p.secondLastName && String(p.secondLastName).trim() ? true : false;
    },
    validation() {
      if (!this.form.dependientes.length) return false;
      let ok = true;
      for (let i = 0; i < this.form.dependientes.length; i++) {
        ok =
          ok &&
          this.getValidationName(i) &&
          this.getValidationLastName(i) &&
          this.getValidationDocumentType(i) &&
          this.getValidationDocument(i) &&
          this.getValidationBirthDay(i) &&
          this.getValidationRelation(i) &&
          this.getValidationWeight(i) &&
          this.getValidationStature(i) &&
          this.getValidationSex(i) &&
          this.getValidationEps(i);
      }
      return ok;
    },
    next() {
      if (this.form.titularEsAsegurado !== "S" && this.form.titularEsAsegurado !== "N") {
        this.$bvToast.toast("Indique si el titular será uno de los asegurados.", {
          title: "Información incompleta",
          variant: "danger",
          solid: true,
        });
        return;
      }
      if (!this.form.dependientes.length) {
        this.$bvToast.toast("Agregue al menos una persona a asegurar.", {
          title: "Información incompleta",
          variant: "danger",
          solid: true,
        });
        return;
      }
      if (!this.validation()) {
        this.$bvToast.toast("Complete todos los datos de cada persona agregada.", {
          title: "Información incompleta",
          variant: "danger",
          solid: true,
        });
        return;
      }
      this.$store.dispatch("wizard/save", { index: 2, form: this.form }).then(() => {
        if (this.form.actionCode.includes("CO303")) {
          this.$router.push("/form/colombia-salud-7.html");
        } else {
          this.$router.push("/form/colombia-salud-3.html");
        }
      });
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
}
.persona-asegurar-card .persona-asegurar-inner {
  border-radius: 0.35rem;
}
.persona-asegurar-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
